import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { usePricingData } from "@/features/pricing/hooks/use-pricing-data";
import { usePricingActions } from "@/features/pricing/hooks/use-pricing-actions";
import { PricingCardMobile } from "@/features/pricing/components/PricingCardMobile";
import { PricingCardDesktop } from "@/features/pricing/components/PricingCardDesktop";
import { BillingCycleToggle } from "@/features/pricing/components/BillingCycleToggle";
import { LoadingSpinner } from "@/components/common/loading-spinner";
import { ErrorState } from "@/components/common/error-state";
import { useTranslation } from "react-i18next";
import useAuthStore from "@/store/use-auth-store";
import { useBillingHistory } from "@/api/hooks/use-query-api";
import { PricingPlan } from "@/api/models/api-types";
import { usePricingStore } from "@/features/pricing/store/pricing-store";
import mixpanel from "mixpanel-browser";
import { ShieldCheck, Shield, ArrowLeft } from "lucide-react";

const PricingPage: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // URL'deki ?cycle= parametresini oku ve store'u senkronize et
  const { selectedBillingCycle, setSelectedBillingCycle } = usePricingStore();

  React.useEffect(() => {
    const cycleParam = searchParams.get("cycle") as
      | "monthly"
      | "yearly"
      | "one-time"
      | null;
    if (
      cycleParam &&
      ["monthly", "yearly", "one-time"].includes(cycleParam)
    ) {
      setSelectedBillingCycle(cycleParam);
    }
  }, [searchParams, setSelectedBillingCycle]);

  const { topupPlans, monthlyPlans, yearlyPlans, isLoading, error } =
    usePricingData({
      enabled: !!user,
    });

  const { data: billingData } = useBillingHistory();

  const {
    changeBillingCycle,
    handleSubscribe: processSubscription,
    handleBuyCredits: processBuyCredits,
    loadingPlanId,
  } = usePricingActions();

  const currentPlan = billingData?.data?.pricingPlan;
  const subscription = billingData?.data?.subscription;

  const isCurrentPlanExpired = React.useMemo(() => {
    if (!subscription || !subscription.expiresAt) return false;
    const expiresAt = new Date(subscription.expiresAt);
    return expiresAt < new Date();
  }, [subscription]);

  const handleSubscribe = React.useCallback(
    (plan: PricingPlan) => {
      try {
        if (typeof mixpanel?.track === "function") {
          mixpanel.track("pricing_plan_selected", {
            plan_id: plan._id,
            plan_name: plan.name,
            price: plan.discountedPrice.toString(),
            currency: plan.currency || "USD",
            plan_type: plan.planType,
          });
        }
      } catch (errorResponse) {
        console.warn("Mixpanel track failed:", errorResponse);
      }

      try {
        const netmera = window.netmera;
        if (netmera?.push) {
          netmera.push(function (api) {
            api.sendEvent({
              code: "bvqch",
              ee: plan.currency || "USD",
              ea: plan._id,
              eb: plan.discountedPrice.toString(),
            });
          });
        }
      } catch (errorResponse) {
        console.warn("Netmera event failed:", errorResponse);
      }

      if (plan.planType === "recurring") {
        processSubscription(plan);
      } else {
        processBuyCredits(plan);
      }
    },
    [processSubscription, processBuyCredits]
  );

  const displayPlans = React.useMemo(() => {
    if (selectedBillingCycle === "one-time") return topupPlans;
    if (selectedBillingCycle === "monthly") return monthlyPlans;
    return yearlyPlans;
  }, [selectedBillingCycle, topupPlans, monthlyPlans, yearlyPlans]);

  const showOneTimeOption = topupPlans.length > 0;

  // URL güncellemesini BillingCycleToggle içindeki `<Link>` üstleniyor;
  // burada yalnızca store senkronizasyonu kalıyor.
  const handleCycleChange = (cycle: "monthly" | "yearly" | "one-time") => {
    changeBillingCycle(cycle);
  };

  return (
    <div className="h-full overflow-y-auto bg-bg-primary dark:bg-bg-primary flex flex-col">
      {/* Sticky Header */}
      <div className="hidden md:block w-full border-b border-border-secondary dark:border-border-secondary sticky top-0 z-10 bg-bg-primary dark:bg-bg-primary">
        <div className="py-4 px-6 md:px-10 max-w-[1400px] mx-auto">
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate(-1)}
              className="p-1.5 rounded-md hover:bg-bg-secondary dark:hover:bg-bg-secondary transition-colors shrink-0"
            >
              <ArrowLeft className="w-5 h-5 text-text-secondary dark:text-text-secondary" />
            </button>
            <div>
              <h1 className="text-xl font-semibold text-text-primary dark:text-text-primary leading-7">
                {t("pricing.plans")}
              </h1>
              <p className="text-sm text-text-tertiary dark:text-text-tertiary leading-5">
                {t("pricing.viewCurrentPlan")}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 px-6 md:px-10 pt-6 pb-8 max-w-[1400px] mx-auto w-full">
        {/* Billing Cycle Toggle — ortalanmış */}
        <div className="flex justify-center mb-8">
          <BillingCycleToggle
            selectedCycle={selectedBillingCycle}
            onCycleChange={handleCycleChange}
            showOneTime={showOneTimeOption}
          />
        </div>

        {/* Plans */}
        {isLoading ? (
          <div className="flex justify-center items-center py-24">
            <LoadingSpinner />
          </div>
        ) : error ? (
          <ErrorState
            title={t("pricing.pricingPlansError")}
            description={t("pricing.pricingPlansErrorDescription")}
          />
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 lg:gap-5">
              {displayPlans.map((plan) => (
                <React.Fragment key={plan._id}>
                  <div className="block sm:hidden">
                    <PricingCardMobile
                      plan={plan}
                      currentPlanId={currentPlan?._id}
                      isCurrentPlanExpired={isCurrentPlanExpired}
                      onSubscribe={handleSubscribe}
                      isLoading={loadingPlanId === plan._id}
                    />
                  </div>
                  <div className="hidden sm:block">
                    <PricingCardDesktop
                      plan={plan}
                      currentPlanId={currentPlan?._id}
                      isCurrentPlanExpired={isCurrentPlanExpired}
                      onSubscribe={handleSubscribe}
                      isLoading={loadingPlanId === plan._id}
                    />
                  </div>
                </React.Fragment>
              ))}
            </div>

            {displayPlans.length === 0 && (
              <div className="text-center py-20 px-4">
                <p className="text-gray-500 dark:text-gray-400 text-sm">
                  {t("pricing.noPlansAvailable")}
                </p>
              </div>
            )}

            {/* Support Section — yatay */}
            <div className="mt-8">
              <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {t("pricing.needHelp")}
                </p>
                <a
                  href="mailto:support@harmonyai.com"
                  className="inline-flex shrink-0 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 font-semibold text-sm px-4 py-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors duration-200"
                >
                  {t("pricing.contactUs")}
                </a>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Trust Bar — sticky bottom */}
      <div className="border-t border-gray-100 dark:border-gray-800 bg-bg-primary dark:bg-bg-primary px-6 py-3">
        <div className="flex items-center justify-center gap-6 flex-wrap">
          <div className="flex items-center gap-1.5">
            <div className="w-4 h-4 rounded-full bg-[#635BFF] flex items-center justify-center shrink-0">
              <span className="text-white text-[8px] font-extrabold leading-none">S</span>
            </div>
            <span className="text-[11px] font-medium text-gray-500 dark:text-gray-400">
              {t("pricing.securePaymentStripe")}
            </span>
          </div>
          <div className="h-3.5 w-px bg-gray-200 dark:bg-gray-700" />
          <div className="flex items-center gap-1.5">
            <ShieldCheck className="w-4 h-4 text-gray-400 dark:text-gray-500 shrink-0" />
            <span className="text-[11px] font-medium text-gray-500 dark:text-gray-400">
              {t("pricing.sslEncrypted")}
            </span>
          </div>
          <div className="h-3.5 w-px bg-gray-200 dark:bg-gray-700" />
          <div className="flex items-center gap-1.5">
            <Shield className="w-4 h-4 text-gray-400 dark:text-gray-500 shrink-0" />
            <span className="text-[11px] font-medium text-gray-500 dark:text-gray-400">
              {t("pricing.gdprCompliant")}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingPage;
